import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';

import { AlertService, UIService } from 'src/app/_services';
import { dropDuplicateContents } from 'src/app/_services/media-import.service';
import { count, duplicateCheck, ExportCheck, reportLines, reportSummary } from 'src/app/_utils/check-export';
import { formatBytes } from 'src/app/_utils/utils';

/** Checks one picked export: in a web worker in the app (WorkerExportChecker), directly in tests */
export abstract class ExportChecker {
  abstract check(name: string, file: File): Promise<ExportCheck>;
  /** drop the check in flight */
  abstract stop(): void;
}

/** A picked file, named by its path within a picked or dropped folder */
interface Picked {
  file: File;
  name: string;
}

interface Entry extends Picked {
  id: number;
  /** missing until the file has been checked */
  check?: ExportCheck;
  /** opened or closed by hand; until then only a failed file is open */
  open?: boolean;
  showAllProblems?: boolean;
}

/** problems shown before "Show more" */
const FIRST_PROBLEMS = 5;

const STATUS = {
  pass: { label: 'Passed', icon: 'fa-check-circle text-success', badge: 'badge-success' },
  warn: { label: 'Passed with warnings', icon: 'fa-exclamation-triangle text-warning', badge: 'badge-warning' },
  fail: { label: 'Failed', icon: 'fa-times-circle text-danger', badge: 'badge-danger' },
};

// the checker leads each finding with where it is, e.g. `Media.BackUpSession[3].BackupDate: missing`
const LOCATED = /^(Media(?:\.\w+|\[\d+\])*): (.*)$/;

const isJsonName = (name: string) => name.toLowerCase().endsWith('.json');

const readBatch = (reader: FileSystemDirectoryReader) =>
  new Promise<FileSystemEntry[]>((resolve, reject) => reader.readEntries(resolve, reject));

/** Every file in the dropped files and folders */
async function filesFromEntries(roots: FileSystemEntry[]): Promise<Picked[]> {
  const files: Picked[] = [];
  const walk = async (entry: FileSystemEntry): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) => (entry as FileSystemFileEntry).file(resolve, reject));
      files.push({ file, name: entry.fullPath.replace(/^\//, '') });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      // a directory's entries come in batches until an empty one
      for (let batch = await readBatch(reader); batch.length > 0; batch = await readBatch(reader)) {
        for (const child of batch) {
          await walk(child);
        }
      }
    }
  };
  for (const root of roots) {
    await walk(root);
  }
  return files;
}

/**
 * Superadmin page, as invenire-v2's Validate JSON: check JSON media exports by the rules a project
 * upload applies, in the browser, before uploading them anywhere
 */
@Component({
    selector: 'app-validate-json',
    templateUrl: './validate-json.component.html',
    styleUrls: ['./validate-json.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ValidateJsonComponent implements OnInit, OnDestroy {
  entries: Entry[] = [];
  checkingId: number | null = null;
  /** what was skipped from the last files picked */
  notice = '';
  dropActive = false;
  readonly count = count;
  readonly formatBytes = formatBytes;
  private dragDepth = 0;
  private nextId = 0;
  private queue: Entry[] = [];
  private running = false;
  // bumped by Clear, so checks still in flight are dropped
  private generation = 0;

  constructor(private checker: ExportChecker, private alert: AlertService, private uiservice: UIService) { }

  ngOnInit() {
    this.uiservice.setTitle('Validate JSON');
  }

  ngOnDestroy() {
    this.checker.stop();
  }

  get checks() {
    return this.entries.flatMap(({ check }) => check ? [check] : []);
  }

  get checking() {
    return this.checks.length < this.entries.length;
  }

  get summary() {
    return this.checking ? `Checking ${this.checks.length + 1} of ${count(this.entries.length, 'file')}…` : reportSummary(this.checks);
  }

  browse(input: HTMLInputElement) {
    const files = Array.from(input.files ?? []);
    input.value = '';
    this.add(files.map(file => ({ file, name: file.webkitRelativePath || file.name })));
  }

  dragEnter(event: DragEvent) {
    event.preventDefault();
    this.dragDepth++;
    this.dropActive = true;
  }

  // entering a child leaves the zone first, so only the last leave counts
  dragLeave() {
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    this.dropActive = this.dragDepth > 0;
  }

  drop(event: DragEvent) {
    event.preventDefault();
    this.dragDepth = 0;
    this.dropActive = false;
    // entries must be taken during the drop event: the list is emptied once it returns
    const roots = Array.from(event.dataTransfer?.items ?? [], item => item.webkitGetAsEntry()).filter(entry => entry !== null);
    filesFromEntries(roots).then(picked => this.add(picked), () => this.alert.error('Could not read the dropped files'));
  }

  /** List the .json files picked and check them; a copy of one listed fails without being checked */
  async add(picked: Picked[]) {
    const generation = this.generation;
    const json = picked.filter(({ name }) => isJsonName(name));
    const skipped = picked.length - json.length;
    this.notice = !skipped ? '' : skipped === 1 ? 'Skipped 1 file that isn’t JSON' : `Skipped ${skipped} files that aren’t JSON`;

    // the same file picked again
    const listed = new Set(this.entries.map(({ name, file }) => `${name}\n${file.size}`));
    const fresh = json.filter(({ name, file }) => !listed.has(`${name}\n${file.size}`));
    if (!fresh.length) { return; }

    let copies: Map<File, File>;
    try {
      const { skipped: same } = await dropDuplicateContents(this.entries.map(({ file }) => file), fresh.map(({ file }) => file));
      copies = new Map(same.map(({ file, sameAs }) => [file, sameAs]));
    } catch {
      this.alert.error('Could not read the selected files');
      return;
    }
    if (generation !== this.generation) { return; }

    const names = new Map([...this.entries, ...fresh].map(({ file, name }) => [file, name]));
    const added = fresh.map(({ file, name }): Entry => {
      const original = copies.get(file);
      return { id: this.nextId++, file, name, check: original && duplicateCheck(name, file.size, names.get(original) ?? '') };
    });
    this.entries = [...this.entries, ...added];
    this.queue.push(...added.filter(entry => !entry.check));
    if (!this.running) { this.checkQueued(); }
  }

  clear() {
    this.generation++;
    this.checker.stop();
    this.queue = [];
    this.running = false;
    this.checkingId = null;
    this.entries = [];
    this.notice = '';
  }

  downloadReport() {
    const checks = this.checks;
    const report = [
      `Checked ${count(checks.length, 'JSON media export')}`,
      '',
      ...checks.flatMap(check => reportLines(check)),
      '',
      reportSummary(checks),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([report], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'validate-json-report.txt';
    link.click();
    URL.revokeObjectURL(url);
  }

  statusOf(check: ExportCheck) {
    return STATUS[check.problemCount > 0 ? 'fail' : check.warningCount > 0 ? 'warn' : 'pass'];
  }

  hasDetails(check: ExportCheck) {
    return check.problems.length > 0 || check.warnings.length > 0;
  }

  isOpen(entry: Entry) {
    return this.hasDetails(entry.check) && (entry.open ?? entry.check.problemCount > 0);
  }

  shownProblems({ check, showAllProblems }: Entry) {
    return showAllProblems ? check.problems : check.problems.slice(0, FIRST_PROBLEMS);
  }

  meta(check: ExportCheck) {
    return [
      formatBytes(check.size, 1),
      // unknown when the file wasn't read; zero when it isn't readable JSON
      ...(check.sessionCount ? [count(check.recordCount ?? 0, 'file record'), count(check.sessionCount, 'session')] : []),
    ].join(' · ');
  }

  located(finding: string) {
    const [, path, message] = LOCATED.exec(finding) ?? [];
    return path ? { path, message } : { path: '', message: finding };
  }

  // one file at a time, so only one export is held in memory
  private async checkQueued() {
    const generation = this.generation;
    this.running = true;
    for (let entry = this.queue.shift(); entry; entry = this.queue.shift()) {
      this.checkingId = entry.id;
      const check = await this.checker.check(entry.name, entry.file);
      if (generation !== this.generation) { return; }
      entry.check = check;
    }
    this.checkingId = null;
    this.running = false;
  }
}
